<template>
    <div class="celebration-overlay" :class="{ show: showCelebration }">
        <confetti-container :show-confetti="showCelebration"></confetti-container>
        <div class="celebration-card">
            <div class="deco-icon star">⭐</div>
            <div class="deco-icon heart">❤️</div>
            <div class="deco-icon surprise">🎊</div>
            
            <app-celebration class="celebration-content" @close="hideCelebration"></app-celebration>
            
            <div class="auto-close-tag">Auto-closing soon...</div>
        </div>
    </div>
</template>

<script>
import { axiosGet } from "../../../common/Helper/AxiosHelper";
import { PUBLIC_DASHBOARD } from "../../../tenant/Config/ApiUrl";

export default {
    name: "PublicDashboardOverlay",
    data() {
        return {
            showCelebration: false,
        };
    },
    mounted() {
        window.addEventListener('show-celebration-overlay', this.showCelebrationHandler);
    },
    beforeDestroy() {
        window.removeEventListener('show-celebration-overlay', this.showCelebrationHandler);
    },
    methods: {
        showCelebrationHandler() {
            // Check backend for any calendar events / birthdays / anniversaries
            axiosGet(`${PUBLIC_DASHBOARD}/calendar-events`).then(({ data }) => {
                const hasTodayBirthday = (data.birthdays || []).some(b => b.day === 'Today');
                const hasTodayAnniversary = (data.anniversaries || []).some(a => a.day === 'Today');
                const hasTodayEvent = data.countdown && data.countdown.title && data.countdown.title !== 'No upcoming events' && data.countdown.days === 0;

                if (hasTodayBirthday || hasTodayAnniversary || hasTodayEvent) {
                    this.showCelebration = true;

                    // Auto hide after 6 seconds
                    setTimeout(() => {
                        this.hideCelebration();
                    }, 6000);
                } else {
                    this.hideCelebration();
                }
            }).catch(() => {
                // On error, do not show the overlay to avoid false positives
            });
        },
        hideCelebration() {
            this.showCelebration = false;
        }
    }
}
</script>

<style scoped>
    /* Overlay with Blur */
    .celebration-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(10px);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 99999;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.5s ease;
    }

    .celebration-overlay.show {
        opacity: 1;
        pointer-events: all;
    }

    /* THE CARD: Size and Glow fixed */
    .celebration-card {
        background: linear-gradient(135deg, #ff6b00, #e11d48);
        padding: 60px 80px;
        min-width: 550px; /* Essential: Fixes the tiny card issue */
        border-radius: 45px;
        border: 8px solid #ffffff;
        
        /* The Glow Effect from image */
        box-shadow: 0 0 50px rgba(255, 255, 255, 0.4), 
                    0 30px 60px rgba(0, 0, 0, 0.5);
        
        text-align: center;
        position: relative;
        
        /* Apply Animation Style 67 Floating */
        /* Animation: 4s or 5s makes it feel like a calm sea wave */
        animation: celebration-wave-67 5s ease-in-out infinite;
        
        /* Smoothness settings */
        will-change: transform;
        perspective: 1000px;
        transform-style: preserve-3d;
    }

    .celebration-overlay.show .celebration-card {
        transform: scale(1);
    }

    /* THE RAIN (Confetti Drop) */
    .confetti-rain {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
    }

    .confetti-rain span {
        position: absolute;
        top: -20px;
        width: 12px;
        height: 24px;
        background: #fff;
        opacity: 0.8;
        border-radius: 4px;
        animation: rain-fall 3.5s linear infinite;
    }

    /* Randomize Rain Drops */
    .confetti-rain span:nth-child(4n) { background: #ffea00; }
    .confetti-rain span:nth-child(4n+1) { background: #00e5ff; }
    .confetti-rain span:nth-child(4n+2) { background: #ff0055; }
    
    /* Spread out the rain across the width */
    .confetti-rain span:nth-child(1) { left: 5%; animation-delay: 0s; }
    .confetti-rain span:nth-child(2) { left: 15%; animation-delay: 1.2s; }
    .confetti-rain span:nth-child(3) { left: 25%; animation-delay: 0.5s; }
    .confetti-rain span:nth-child(4) { left: 35%; animation-delay: 2.1s; }
    .confetti-rain span:nth-child(5) { left: 45%; animation-delay: 0.8s; }
    .confetti-rain span:nth-child(6) { left: 55%; animation-delay: 1.5s; }
    .confetti-rain span:nth-child(7) { left: 65%; animation-delay: 2.5s; }
    .confetti-rain span:nth-child(8) { left: 75%; animation-delay: 0.3s; }
    .confetti-rain span:nth-child(9) { left: 85%; animation-delay: 1.9s; }
    .confetti-rain span:nth-child(10) { left: 95%; animation-delay: 0.7s; }

    @keyframes rain-fall {
        0% { transform: translateY(-50px) rotate(0deg); }
        100% { transform: translateY(110vh) rotate(720deg); }
    }

    /* Keyframe 67: Smooth Floating */

    @keyframes celebration-wave-67 {
        0% {
            transform: translate3d(0, 0, 0) rotate(0deg);
        }
        33% {
            /* Rolling up and to the right */
            transform: translate3d(15px, -10px, 0) rotate(1.5deg);
        }
        66% {
            /* Rolling down and to the left */
            transform: translate3d(-15px, 10px, 0) rotate(-1.5deg);
        }
        100% {
            transform: translate3d(0, 0, 0) rotate(0deg);
        }
    }

    /* Decorative Floating Icons */
    .deco-icon {
        position: absolute;
        font-size: 4rem;
        z-index: 11;
    }
    .star { top: -25px; left: -25px; animation: spin 6s linear infinite; }
    .heart { bottom: 20px; left: -35px; animation: float-icon 3s ease-in-out infinite; }
    .surprise { top: -25px; right: -25px; animation: shake-icon 1.2s ease-in-out infinite; }

    @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
    @keyframes float-icon { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
    @keyframes shake-icon { 0%, 100% { transform: rotate(0); } 50% { transform: rotate(15deg) scale(1.2); } }

    .auto-close-tag {
        margin-top: 30px;
        background: #ffffff;
        color: #e11d48;
        padding: 8px 25px;
        border-radius: 50px;
        font-weight: 800;
        display: inline-block;
        box-shadow: 0 5px 15px rgba(0,0,0,0.1);
    }
</style>
